/* Client-side Proof-of-Delivery (POD) generator.

   Produces a 2-page PDF that visually matches the courier's own POD (white card on grey, two-column
   label:value grid, courier logo, faint "Delivered" watermark, bordered Proof-of-Receipt table,
   ID-proof/signature boxes). Rather than hand-positioning text, we render a styled HTML template
   with html2canvas and place each page image into a jsPDF A4 document — this reproduces the exact
   layout, font (Arial/Helvetica, same as the reference) and backgrounds.

   Field sources (order/search response):
     • OTP verified       → order.otpVerifiedDelivery (top-level flag set by the status webhook);
                            receiverDetails.otpVerified is kept only as a legacy fallback
     • latitude/longitude → the DELIVERED order_history event's `additional` (receiverDetails is
                            usually null); falls back to receiverDetails.
   These are populated by Delhivery; gate the trigger on a delivered Delhivery order (podAvailable). */
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { courierLogo } from '../../common/util'

// Delivered scan carries the doorstep geo + final remark; take the last DELIVERED event.
export function getDeliveredEvent(order) {
  const hist = Array.isArray(order?.order_history) ? order.order_history : []
  const delivered = hist.filter((e) => e?.orderStatusEnum === 'DELIVERED' || e?.orderStatusCode === 8)
  return delivered.length ? delivered[delivered.length - 1] : null
}

export function podAvailable(order) {
  if (!order || order.orderStatus !== 'DELIVERED') return false
  return /delhivery/i.test(order?.courierDetail?.parent || order?.actualCourierProviderName || '')
}

function coords(order) {
  const ev = getDeliveredEvent(order)
  return {
    lat: ev?.additional?.latitude ?? order?.receiverDetails?.latitude ?? null,
    lng: ev?.additional?.longitude ?? order?.receiverDetails?.longitude ?? null,
  }
}

// IST, matching the courier's own POD ("24 Mar 2026 12:17").
function fmtDate(dt) {
  if (!dt) return '—'
  try {
    return new Date(dt)
      .toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
      .replace(',', '')
  } catch { return '—' }
}

function esc(v) {
  if (v == null || v === '') return '—'
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function fullAddress(d) {
  if (!d) return '—'
  return esc([d.to_address, d.to_city, d.to_state, d.to_pincode].filter(Boolean).join(', '))
}

// A label:value block (bold label with colon, bold value below) — the reference's field style.
const field = (label, value, { valueSize = 15 } = {}) => `
  <div>
    <div style="font-size:13px;font-weight:bold;color:#111;">${label}:</div>
    <div style="font-size:${valueSize}px;font-weight:bold;color:#111;margin-top:6px;line-height:1.4;">${value}</div>
  </div>`

function page1Html(order, logoSrc) {
  const { lat, lng } = coords(order)
  const ev = getDeliveredEvent(order)
  const awb = esc(order?.awb_number || order?.awbNumber)
  const courier = esc(order?.courierDetail?.parent || order?.actualCourierProviderName || 'the courier partner')
  const otp = order?.otpVerifiedDelivery === true || order?.receiverDetails?.otpVerified === true
  const cell = 'border:1px solid #e6e8eb;padding:11px 14px;'
  return `
  <div style="width:794px;min-height:1123px;box-sizing:border-box;background:#f4f5f7;padding:24px;font-family:Arial,Helvetica,sans-serif;">
    <div style="position:relative;background:#fff;border:1px solid #e6e8eb;border-radius:10px;box-shadow:0 1px 4px rgba(0,0,0,.06);padding:42px 46px;min-height:1051px;box-sizing:border-box;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;">
        <div style="max-width:72%;">
          <div style="font-size:26px;font-weight:bold;color:#111;">Proof of Delivery (POD)</div>
          <div style="font-size:12px;color:#6b7280;margin-top:8px;line-height:1.5;">The delivery of tracking ID ${awb} is done by ${courier} and below details are provided as proof of delivery.</div>
        </div>
        ${logoSrc ? `<img src="${logoSrc}" style="height:46px;max-width:170px;object-fit:contain;" />` : ''}
      </div>
      <div style="border-top:1px solid #e6e8eb;margin:20px 0 30px;"></div>

      <div style="position:absolute;top:44%;left:50%;transform:translate(-50%,-50%) rotate(-22deg);width:290px;height:290px;border:6px solid #e9ebee;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#e0e3e7;font-size:58px;font-weight:bold;pointer-events:none;">Delivered</div>

      <div style="position:relative;display:grid;grid-template-columns:1fr 1fr;row-gap:28px;column-gap:24px;">
        ${field('Waybill', awb)}
        ${field('Delivered on', esc(fmtDate(order?.actualDeliveryTime || ev?.timestamp)))}
        ${field('Order ID', esc(order?.reference || order?.orderId))}
        ${field('Payment Method', esc(order?.payment_mode))}
        ${field('Delivery Center', esc(ev?.currentLocation))}
        ${field('Consignee Name', esc(order?.delivery_details?.to_name))}
        <div style="grid-column:1 / -1;">${field('Address', fullAddress(order?.delivery_details))}</div>
      </div>

      <div style="border-top:1px solid #e6e8eb;margin:32px 0 18px;"></div>
      <div style="font-size:18px;font-weight:bold;color:#111;margin-bottom:16px;">Proof of Receipt</div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <tr><td style="${cell}color:#6b7280;width:34%;">OTP Verified Delivery</td><td style="${cell}font-weight:bold;color:${otp ? '#108559' : '#111'};">${otp ? 'Yes' : 'No'}</td></tr>
        <tr><td style="${cell}color:#6b7280;">Latitude</td><td style="${cell}font-weight:bold;">${lat != null ? esc(lat) : '—'}</td></tr>
        <tr><td style="${cell}color:#6b7280;">Longitude</td><td style="${cell}font-weight:bold;">${lng != null ? esc(lng) : '—'}</td></tr>
      </table>

      <div style="position:absolute;left:46px;right:46px;bottom:34px;border-top:1px solid #e6e8eb;padding-top:12px;font-size:10px;color:#9ca3af;">Disclaimer: This document is generated by Proship using delivery information received from the courier partner.</div>
    </div>
  </div>`
}

function page2Html(order) {
  const ev = getDeliveredEvent(order)
  const otp = order?.otpVerifiedDelivery === true || order?.receiverDetails?.otpVerified === true
  const boxLabel = 'font-size:13px;font-weight:bold;color:#111;margin-bottom:10px;'
  return `
  <div style="width:794px;min-height:1123px;box-sizing:border-box;background:#fff;padding:44px 48px;font-family:Arial,Helvetica,sans-serif;position:relative;">
    <div style="display:flex;align-items:center;gap:10px;">
      <div style="width:26px;height:26px;border-radius:50%;background:#e8552d;color:#fff;font-weight:bold;font-size:16px;display:flex;align-items:center;justify-content:center;">D</div>
      <div style="font-size:20px;font-weight:bold;color:#111;">Proof of delivery (POD)</div>
    </div>
    <div style="border-top:1.5px solid #333;margin:16px 0 30px;"></div>

    <div style="position:absolute;top:230px;right:120px;width:220px;height:220px;border:4px solid #dfe6df;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#cfd9cf;font-size:34px;font-weight:bold;transform:rotate(-18deg);pointer-events:none;">[Delivered]</div>

    <div style="display:grid;grid-template-columns:1fr 1fr;row-gap:30px;column-gap:24px;">
      ${field('Waybill', esc(order?.awb_number || order?.awbNumber))}
      ${field('Delivered on', esc(fmtDate(order?.actualDeliveryTime || ev?.timestamp)))}
      ${field('Order ID', esc(order?.reference || order?.orderId))}
      <div></div>
      ${field('Consignee Name', esc(order?.delivery_details?.to_name))}
      ${field('Final Status', esc(ev?.remark || 'Delivered to consignee'), { valueSize: 14 })}
      <div style="grid-column:1 / -1;">${field('Address', fullAddress(order?.delivery_details))}</div>
      <div style="grid-column:1 / -1;">${field('Collected by information', esc(ev?.additional?.receiver_name || order?.delivery_details?.to_name))}</div>
    </div>

    <div style="border-top:1px solid #d5d5d5;margin:34px 0 22px;"></div>
    <div style="font-size:16px;font-weight:bold;color:#111;margin-bottom:22px;">Proof of Receipt:</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;column-gap:40px;">
      <div>
        <div style="${boxLabel}">ID Proof:</div>
        <div style="height:150px;background:#f5f5f5;border:1px solid #e6e8eb;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#9aa1ab;font-size:14px;">Image not available</div>
      </div>
      <div>
        <div style="${boxLabel}">Signature:</div>
        <div style="height:150px;border:1px solid #e6e8eb;border-radius:4px;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#374151;font-size:18px;font-weight:bold;text-align:center;">
          ${otp ? 'Signature not required<div style="font-size:13px;color:#6b7280;margin-top:6px;">— Delivered with OTP —</div>' : 'Signature not captured'}
        </div>
      </div>
    </div>

    <div style="border-top:1px solid #d5d5d5;margin:34px 0 16px;"></div>
    <div style="font-size:11px;font-weight:bold;color:#111;line-height:1.5;">Disclaimer: This document is proprietary and for internal use only. No part of this document may be disclosed in any manner to a third party without the prior written consent of the courier partner.</div>
    <div style="text-align:center;font-style:italic;font-weight:bold;font-size:11px;color:#111;margin-top:20px;">Copyright @ ${new Date().getFullYear()}, Delhivery Pvt Ltd, All rights reserved.</div>
  </div>`
}

// Load a same-origin asset and inline it as a data URL. Embedding the logo this way (rather than a
// bare <img src="/ship/..">) makes html2canvas render it reliably — no CORS re-fetch, no load-timing
// race. Returns null on any failure so the template just omits the logo gracefully.
function loadImageDataUrl(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null)
    const img = new Image()
    img.onload = () => {
      try {
        const c = document.createElement('canvas')
        c.width = img.naturalWidth || 200
        c.height = img.naturalHeight || 60
        c.getContext('2d').drawImage(img, 0, 0)
        resolve(c.toDataURL('image/png'))
      } catch { resolve(null) }
    }
    img.onerror = () => resolve(null)
    img.src = src
  })
}

// courierLogo() matches on the substring "delivery", which — surprisingly — is NOT contained in
// "delhivery" (the 'h' breaks it), so it returns undefined for Delhivery. Resolve that case
// explicitly to the shipped asset, and fall back to courierLogo() for other couriers.
function courierLogoPath(order) {
  const name = (order?.courierDetail?.parent || order?.courierDetail?.name || order?.actualCourierProviderName || '').toLowerCase()
  if (name.includes('delhivery') || name.includes('delivery')) return '/ship/delivery.png'
  return courierLogo(name) || null
}

export async function downloadPOD(order) {
  const logoSrc = await loadImageDataUrl(courierLogoPath(order))

  // Off-screen host so html2canvas can measure/paint real layout.
  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:-10000px;top:0;'
  host.innerHTML = `<div class="pod-pg">${page1Html(order, logoSrc)}</div><div class="pod-pg">${page2Html(order)}</div>`
  document.body.appendChild(host)

  try {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const pages = host.querySelectorAll('.pod-pg')
    for (let i = 0; i < pages.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(pages[i].firstElementChild, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })
      const img = canvas.toDataURL('image/jpeg', 0.92)
      const h = Math.min(pageH, (canvas.height / canvas.width) * pageW)
      if (i > 0) doc.addPage()
      doc.addImage(img, 'JPEG', 0, 0, pageW, h)
    }
    doc.save(`POD_${order?.awb_number || 'order'}.pdf`)
  } finally {
    document.body.removeChild(host)
  }
}
